import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

export default function AddressAutocomplete({
  defaultValue = "",
  onPlaceSelect,
  placeholder = "Search for a business or address...",
  className = "",
  disabled = false,
  searchTypes = ["establishment"],  // "establishment" for businesses, "address" for addresses, [] for both
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const [isLoading, setIsLoading] = useState(false);

  // Keep the callback ref updated
  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  // Set default value when it changes
  useEffect(() => {
    if (inputRef.current && defaultValue) {
      inputRef.current.value = defaultValue;
    }
  }, [defaultValue]);

  // Initialize autocomplete once Google Maps is loaded
  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google?.maps?.places) {
        return false;
      }

      const input = inputRef.current;
      if (!input || autocompleteRef.current) return true;

      console.log("Initializing Google Places Autocomplete...");

      // Initialize autocomplete with expanded fields for business data
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: searchTypes,
        componentRestrictions: { country: "au" },
        fields: [
          "address_components", "geometry", "formatted_address", "name", "place_id",
          "opening_hours", "formatted_phone_number", "website",
          "types", "business_status", "rating", "user_ratings_total",
          "utc_offset_minutes",
        ]
      });

      autocompleteRef.current = autocomplete;

      // Handle place selection
      autocomplete.addListener("place_changed", () => {
        console.log("Place changed event fired");
        setIsLoading(true);
        const place = autocomplete.getPlace();

        console.log("Selected place:", place);

        if (!place.geometry) {
          console.log("No geometry found");
          setIsLoading(false);
          return;
        }

        // Extract address components
        const addressData = {
          formatted_address: place.formatted_address || "",
          street_address: "",
          suburb: "",
          state: "",
          postcode: "",
          country: "Australia",
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          // Places enrichment fields
          place_id: place.place_id || null,
          place_name: place.name || null,
          opening_hours: null,
          phone: place.formatted_phone_number || null,
          website: place.website || null,
          types: place.types || [],
          business_status: place.business_status || null,
          rating: place.rating || null,
          user_ratings_total: place.user_ratings_total || null,
          utc_offset_minutes: place.utc_offset_minutes ?? null,
        };

        // Parse opening hours if available
        if (place.opening_hours) {
          addressData.opening_hours = {
            weekday_text: place.opening_hours.weekday_text || [],
            periods: (place.opening_hours.periods || []).map(p => ({
              open: p.open ? { day: p.open.day, hour: p.open.hours, minute: p.open.minutes || 0 } : null,
              close: p.close ? { day: p.close.day, hour: p.close.hours, minute: p.close.minutes || 0 } : null,
            })),
          };
        }

        // Parse address components
        if (place.address_components) {
          for (const component of place.address_components) {
            const types = component.types;

            if (types.includes("street_number")) {
              addressData.street_address = component.long_name;
            }
            if (types.includes("route")) {
              addressData.street_address = addressData.street_address
                ? `${addressData.street_address} ${component.long_name}`
                : component.long_name;
            }
            if (types.includes("locality") || types.includes("sublocality") || types.includes("sublocality_level_1")) {
              if (!addressData.suburb) {
                addressData.suburb = component.long_name;
              }
            }
            if (types.includes("administrative_area_level_1")) {
              const stateMap = {
                "New South Wales": "NSW",
                "Victoria": "VIC",
                "Queensland": "QLD",
                "Western Australia": "WA",
                "South Australia": "SA",
                "Tasmania": "TAS",
                "Northern Territory": "NT",
                "Australian Capital Territory": "ACT"
              };
              addressData.state = stateMap[component.long_name] || component.short_name;
            }
            if (types.includes("postal_code")) {
              addressData.postcode = component.long_name;
            }
            if (types.includes("country")) {
              addressData.country = component.long_name;
            }
          }
        }

        console.log("Parsed address data:", addressData);

        // Call the callback using ref to ensure we have latest function
        if (onPlaceSelectRef.current) {
          console.log("Calling onPlaceSelect callback");
          onPlaceSelectRef.current(addressData);
        } else {
          console.log("No onPlaceSelect callback found");
        }

        setIsLoading(false);
      });

      return true;
    };

    // Try to initialize immediately
    if (!initAutocomplete()) {
      // If Google Maps isn't loaded yet, poll for it
      const interval = setInterval(() => {
        if (initAutocomplete()) {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    }

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        disabled={disabled}
        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-10 pr-10 ${className}`}
        data-testid="address-autocomplete-input"
      />
      {isLoading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
